import { useTheme } from "@emotion/react";
import type { IconName } from "../../types";

interface MenuIconsProps {
  name: IconName;
  size?: number;
  color?: "primary" | "secondary" | "tertiary" | "disabled" | "inactive" | "white" | "currentColor";
  className?: string;
}

export const MenuIcons = ({
  name,
  size = 24,
  color = "currentColor",
  className,
}: MenuIconsProps) => {
  const theme = useTheme();

  // Map color names to theme values
  const getColor = (colorInput: string): string => {
    switch (colorInput) {
      case "primary":
        return theme.colors.primary[1000];
      case "secondary":
        return theme.colors.secondary[1000];
      case "tertiary":
        return theme.colors.tertiary[1000];
      case "disabled":
        return theme.colors.neutral.greyDark[1000];
      case "inactive":
        return theme.colors.neutral.greyLight[1000];
      case "white":
        return theme.colors.neutral.white;
      default:
        return colorInput;
    }
  };

  const iconColor = getColor(color);
  const scaleFactor = size / 24; // Base size is 24

  const icons: Record<IconName, React.JSX.Element> = {
    home: (
      <svg
        width={28 * scaleFactor}
        height={29 * scaleFactor}
        viewBox="0 0 28 29"
        fill="none"
      >
        <path
          d="M24.3017 9.67861L16.66 3.34694C15.1667 2.11444 12.8333 2.10236 11.3517 3.33486L3.71 9.67861C2.61333 10.5849 1.94833 12.3974 2.18166 13.8232L3.65166 22.934C3.99 24.9761 5.82166 26.5832 7.81666 26.5832H20.1833C22.155 26.5832 24.0217 24.9399 24.36 22.9219L25.83 13.8111C26.04 12.3974 25.375 10.5849 24.3017 9.67861ZM14.875 21.7499C14.875 22.2453 14.4783 22.6561 14 22.6561C13.5217 22.6561 13.125 22.2453 13.125 21.7499V18.1249C13.125 17.6294 13.5217 17.2186 14 17.2186C14.4783 17.2186 14.875 17.6294 14.875 18.1249V21.7499Z"
          fill={iconColor}
        />
      </svg>
    ),
    target: (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
      >
        <g clipPath="url(#clip0_1472_4490)">
          <path
            d="M12.0007 0C13.0759 0 14.1175 0.1416 15.1087 0.4068L12.5575 2.9568C12.364 3.15112 12.1933 3.36692 12.0487 3.6H12.0007C10.3394 3.6 8.71532 4.09265 7.33394 5.01566C5.95257 5.93866 4.87592 7.25056 4.24014 8.78546C3.60437 10.3204 3.43802 12.0093 3.76214 13.6388C4.08625 15.2682 4.88627 16.7649 6.06104 17.9397C7.2358 19.1145 8.73253 19.9145 10.362 20.2386C11.9914 20.5627 13.6804 20.3964 15.2153 19.7606C16.7502 19.1248 18.0621 18.0482 18.9851 16.6668C19.9081 15.2854 20.4007 13.6614 20.4007 12V11.952C20.6327 11.808 20.8467 11.638 21.0427 11.442L23.5939 8.892C23.8591 9.8832 24.0007 10.9248 24.0007 12C24.0007 18.6276 18.6283 24 12.0007 24C5.37313 24 0.000732422 18.6276 0.000732422 12C0.000732422 5.3724 5.37313 0 12.0007 0ZM11.5039 6.0204C11.5039 7.0416 11.4535 8.0772 11.5087 9.0972L10.8607 9.7452C10.6378 9.96811 10.461 10.2327 10.3404 10.524C10.2197 10.8152 10.1576 11.1274 10.1576 11.4426C10.1576 11.7578 10.2197 12.07 10.3404 12.3612C10.461 12.6525 10.6378 12.9171 10.8607 13.14C11.0836 13.3629 11.3483 13.5397 11.6395 13.6604C11.9307 13.781 12.2429 13.8431 12.5581 13.8431C12.8734 13.8431 13.1855 13.781 13.4768 13.6604C13.768 13.5397 14.0326 13.3629 14.2555 13.14L14.9035 12.492C15.9235 12.5472 16.9579 12.4968 17.9803 12.4968C17.8855 13.6372 17.4666 14.7267 16.7729 15.6369C16.0792 16.547 15.1398 17.2398 14.0653 17.6335C12.9908 18.0272 11.8261 18.1054 10.7087 17.859C9.59118 17.6125 8.56748 17.0516 7.75831 16.2424C6.94914 15.4332 6.38824 14.4096 6.14177 13.2921C5.8953 12.1746 5.97354 11.0099 6.36726 9.93544C6.76098 8.86097 7.45375 7.92149 8.36388 7.22781C9.27401 6.53413 10.3635 6.11519 11.5039 6.0204ZM19.8055 0.1524C20.0246 0.243215 20.2118 0.396884 20.3436 0.594011C20.4753 0.791138 20.5458 1.02289 20.5459 1.26V3.456H22.7407C22.978 3.45605 23.21 3.52646 23.4073 3.65832C23.6046 3.79018 23.7583 3.97757 23.8491 4.19681C23.94 4.41605 23.9637 4.65729 23.9174 4.89004C23.8712 5.12278 23.7569 5.33658 23.5891 5.5044L19.3447 9.744C19.1197 9.96906 18.8146 10.0955 18.4963 10.0956H15.6007L13.4071 12.2904C13.182 12.5156 12.8766 12.6421 12.5581 12.6421C12.2397 12.6421 11.9343 12.5156 11.7091 12.2904C11.484 12.0652 11.3575 11.7598 11.3575 11.4414C11.3575 11.123 11.484 10.8176 11.7091 10.5924L13.9039 8.4V5.5032C13.9038 5.34542 13.9348 5.18917 13.9951 5.04339C14.0555 4.8976 14.144 4.76515 14.2555 4.6536L18.4975 0.4116C18.6653 0.243681 18.8792 0.12931 19.112 0.0829577C19.3449 0.0366053 19.5862 0.0603541 19.8055 0.1512"
            fill={iconColor}
          />
        </g>
        <defs>
          <clipPath id="clip0_1472_4490">
            <rect width="24" height="24" fill="white" />
          </clipPath>
        </defs>
      </svg>
    ),
    check: (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
      >
        <g clipPath="url(#clip0_1472_4496)">
          <path
            d="M10.32 14.16L7.74 11.58C7.52 11.36 7.24 11.25 6.9 11.25C6.56 11.25 6.28 11.36 6.06 11.58C5.84 11.8 5.73 12.08 5.73 12.42C5.73 12.76 5.84 13.04 6.06 13.26L9.48 16.68C9.71999 16.92 10 17.04 10.32 17.04C10.64 17.04 10.92 16.92 11.16 16.68L17.94 9.9C18.16 9.68 18.27 9.4 18.27 9.06C18.27 8.72 18.16 8.44 17.94 8.22C17.72 8 17.44 7.89 17.1 7.89C16.76 7.89 16.48 8 16.26 8.22L10.32 14.16ZM12 24C10.34 24 8.78 23.6848 7.32 23.0544C5.86 22.424 4.59 21.5692 3.51 20.49C2.43 19.4108 1.5752 18.1408 0.945601 16.68C0.316001 15.2192 0.000801518 13.6592 1.51899e-06 12C-0.00079848 10.3408 0.314401 8.7808 0.945601 7.32C1.5768 5.8592 2.4316 4.5892 3.51 3.51C4.5884 2.4308 5.8584 1.576 7.32 0.9456C8.7816 0.3152 10.3416 0 12 0C13.6584 0 15.2184 0.3152 16.68 0.9456C18.1416 1.576 19.4116 2.4308 20.49 3.51C21.5684 4.5892 22.4236 5.8592 23.0556 7.32C23.6876 8.7808 24.0024 10.3408 24 12C23.9976 13.6592 23.6824 15.2192 23.0544 16.68C22.4264 18.1408 21.5716 19.4108 20.49 20.49C19.4084 21.5692 18.1384 22.4244 16.68 23.0556C15.2216 23.6868 13.6616 24.0016 12 24Z"
            fill={iconColor}
          />
        </g>
        <defs>
          <clipPath id="clip0_1472_4496">
            <rect width="24" height="24" fill="white" />
          </clipPath>
        </defs>
      </svg>
    ),
    bag: (
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
      >
        <g clipPath="url(#clip0_1472_4508)">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M19.2396 0C19.4745 0.00183699 19.7036 0.0727771 19.8985 0.204C20.0916 0.329317 20.2486 0.503077 20.3538 0.708C20.4602 0.915714 20.5137 1.14656 20.5095 1.38C20.5096 1.60678 20.4519 1.82982 20.3418 2.028V2.088L19.4553 3.288C20.7505 4.02851 21.8607 5.05438 22.7018 6.288C23.5993 7.72386 24.0497 9.39482 23.9957 11.088C24.0255 11.8806 23.8518 12.6675 23.491 13.3735C23.1302 14.0795 22.5945 14.6809 21.9351 15.12C20.4409 15.991 18.726 16.408 16.9994 16.32C15.2728 16.408 13.5579 15.991 12.0637 15.12C11.4021 14.6889 10.8641 14.0926 10.5028 13.3897C10.1414 12.6869 9.96925 11.9018 10.0032 11.112C9.94917 9.41882 10.3996 7.74786 11.297 6.312C12.1341 5.07178 13.245 4.04119 14.5435 3.3L13.657 2.1V2.04C13.547 1.84182 13.4892 1.61878 13.4893 1.392C13.4814 1.15957 13.5308 0.928776 13.6331 0.72C13.7391 0.51185 13.9009 0.337349 14.1003 0.216C14.2734 0.0932102 14.476 0.0186703 14.6873 0H19.2396ZM17.6942 5.844C17.6942 5.64986 17.6173 5.46367 17.4802 5.3264C17.3432 5.18912 17.1573 5.112 16.9635 5.112C16.7697 5.112 16.5838 5.18912 16.4467 5.3264C16.3097 5.46367 16.2327 5.64986 16.2327 5.844V6.384C15.7895 6.42248 15.3748 6.61908 15.0641 6.93801C14.7534 7.25694 14.5673 7.67702 14.5397 8.12178C14.5122 8.56655 14.6449 9.00645 14.9139 9.36144C15.1828 9.71642 15.57 9.96289 16.0051 10.056L17.5026 10.38C17.6219 10.4072 17.7285 10.4744 17.8047 10.5703C17.8809 10.6663 17.9222 10.7854 17.9219 10.908C17.9187 11.0491 17.8606 11.1834 17.7598 11.2821C17.659 11.3808 17.5237 11.436 17.3828 11.436H16.5442C16.4346 11.4376 16.3273 11.4049 16.2371 11.3425C16.1469 11.2802 16.0784 11.1912 16.041 11.088C16.0142 10.9917 15.968 10.9018 15.9052 10.8241C15.8424 10.7463 15.7644 10.6823 15.6759 10.6359C15.5875 10.5896 15.4905 10.5618 15.3909 10.5545C15.2913 10.5471 15.1913 10.5602 15.097 10.5931C15.0027 10.626 14.9161 10.6778 14.8426 10.7455C14.7691 10.8132 14.7102 10.8952 14.6696 10.9866C14.6289 11.0779 14.6074 11.1766 14.6063 11.2766C14.6053 11.3766 14.6247 11.4758 14.6633 11.568C14.7813 11.9084 14.9898 12.2102 15.2664 12.4407C15.5429 12.6712 15.8771 12.8217 16.2327 12.876V13.44C16.2327 13.5356 16.2517 13.6302 16.2885 13.7183C16.3254 13.8065 16.3794 13.8864 16.4475 13.9534C16.5155 14.0204 16.5961 14.0732 16.6847 14.1087C16.7733 14.1441 16.8681 14.1616 16.9635 14.16C17.1552 14.16 17.3393 14.0846 17.476 13.9499C17.6127 13.8152 17.6911 13.6321 17.6942 13.44V12.876C18.1481 12.7969 18.5607 12.5634 18.8626 12.2148C19.1644 11.8663 19.3369 11.4241 19.351 10.9629C19.3651 10.5017 19.2199 10.0498 18.9399 9.68343C18.6598 9.31707 18.2622 9.05872 17.814 8.952L16.3166 8.628C16.2117 8.61686 16.1156 8.56446 16.0493 8.48232C15.983 8.40018 15.952 8.29503 15.9632 8.19C15.9743 8.08497 16.0266 7.98868 16.1086 7.92229C16.1906 7.8559 16.2956 7.82486 16.4004 7.836H17.3828C17.4575 7.83691 17.5313 7.85337 17.5993 7.88436C17.6674 7.91535 17.7283 7.96018 17.7781 8.016C17.8275 8.06679 17.8645 8.12842 17.8859 8.196C17.9181 8.28666 17.9678 8.37006 18.0322 8.44145C18.0966 8.51284 18.1745 8.57082 18.2612 8.61205C18.348 8.65329 18.4421 8.67699 18.538 8.68178C18.634 8.68658 18.7299 8.67238 18.8204 8.64C19.0012 8.57228 19.1486 8.4367 19.2313 8.26197C19.3141 8.08725 19.3257 7.88712 19.2636 7.704C19.1743 7.46112 19.0402 7.23722 18.8683 7.044C18.5582 6.69271 18.1336 6.46308 17.6703 6.396L17.6942 5.844Z"
            fill={iconColor}
          />
          <path
            d="M18.5209 18.888C17.793 18.1682 16.8123 17.7632 15.7895 17.76H12.8903C12.7234 17.0703 12.3713 16.4395 11.872 15.936C11.5142 15.5731 11.0862 15.2873 10.6142 15.096C10.1487 14.8957 9.64716 14.7936 9.14063 14.796H3.3903V22.524H18.7485C18.868 22.5244 18.9863 22.4999 19.0959 22.452C19.2094 22.4067 19.3116 22.337 19.3954 22.248C19.4781 22.1621 19.543 22.0606 19.5862 21.9493C19.6294 21.8381 19.65 21.7193 19.647 21.6C19.64 20.5833 19.2358 19.6097 18.5209 18.888Z"
            fill={iconColor}
          />
          <path
            d="M1.79698 14.22C1.79698 13.9813 1.70232 13.7524 1.53382 13.5836C1.36532 13.4148 1.13678 13.32 0.898489 13.32C0.660195 13.32 0.431661 13.4148 0.263161 13.5836C0.0946619 13.7524 5.02167e-09 13.9813 0 14.22V23.1C5.02167e-09 23.3387 0.0946619 23.5676 0.263161 23.7364C0.431661 23.9052 0.660195 24 0.898489 24C1.13678 24 1.36532 23.9052 1.53382 23.7364C1.70232 23.5676 1.79698 23.3387 1.79698 23.1V14.22Z"
            fill={iconColor}
          />
        </g>
        <defs>
          <clipPath id="clip0_1472_4508">
            <rect width="24" height="24" fill="white" />
          </clipPath>
        </defs>
      </svg>
    ),
    chart: (
      <svg
        width={28 * scaleFactor}
        height={27 * scaleFactor}
        viewBox="0 0 28 27"
        fill="none"
      >
        <path
          d="M25.2816 7.81858C24.5349 5.37733 22.4233 3.34108 19.8916 2.62108C17.9666 2.08108 16.6366 2.12608 15.7149 2.78983C14.6066 3.58858 14.4783 5.02858 14.4783 6.05233V8.85358C14.4783 11.6211 15.7849 13.0273 18.3516 13.0273H21.6999C22.7499 13.0273 24.2549 12.9036 25.0833 11.8348C25.7949 10.9573 25.8533 9.67483 25.2816 7.81858Z"
          fill={iconColor}
        />
        <path
          d="M22.0615 15.0298C21.7582 14.6923 21.3149 14.5011 20.8599 14.5011H16.6832C14.6299 14.5011 12.9615 12.8923 12.9615 10.9123V6.8848C12.9615 6.44605 12.7632 6.01855 12.4132 5.72605C12.0749 5.43355 11.6082 5.29855 11.1649 5.3548C8.42321 5.6923 5.90321 7.14355 4.25821 9.32605C2.60155 11.5198 1.99488 14.1973 2.51988 16.8748C3.27821 20.7448 6.48655 23.8386 10.5115 24.5698C11.1532 24.6936 11.7949 24.7498 12.4365 24.7498C14.5482 24.7498 16.5899 24.1198 18.3282 22.8936C20.5915 21.3073 22.0965 18.8773 22.4465 16.2336C22.5049 15.7948 22.3649 15.3561 22.0615 15.0298Z"
          fill={iconColor}
        />
      </svg>
    ),
    // Fallback icons to prevent errors
    user: <></>,
    back: <></>,
    alert: <></>,
    plus: <></>,
    minus: <></>,
    "caret-down": <></>,
    "checkmark-circle": <></>,
    calendar: <></>,
    save: <></>,
    logout: <></>,
    union: <></>,
    whatsapp: <></>,
    telegram: <></>,
    copy: <></>,
    "user-alert": <></>,
    menu: <></>,
    play: <></>,
    close: <></>,
  };

  return (
    <span
      className={className}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {icons[name] || icons.home}
    </span>
  );
};
